// Phase 3.11 (hh-14p) — NotifsScreen.
//
// Ported from designproject/files/screens-notifs.jsx (`NotifsScreen`,
// `NotifCard`). The design read a static window.NOTIFS list with pre-baked
// title/body/actions; here the rows come from GET /api/v1/notifications and
// the inline action buttons PATCH /api/v1/notifications/{id} { action }.
//
// The backend row carries only kind / equipmentId / itemId / scheduledFor /
// readAt / actionTaken / createdAt — so the human-readable title, body and
// time label are derived on the client from `kind` and `scheduledFor`.

import type { JSX } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { T, Avatar, SectionLabel, type IconName, type AvatarKind } from '../../tokens';
import { ScreenHeader } from '../../shell';
import type { RouterApi } from '../../router';
import { notifications, humanizeError } from '../../api';
import type {
  NotificationRow,
  NotificationKind,
  NotificationAction,
} from '../../api/endpoints/notifications.js';

export interface NotifsScreenProps {
  readonly router: RouterApi;
}

type Tone = 'neutral' | 'safety' | 'warning';

interface ToneStyle {
  readonly bg: string;
  readonly bd: string;
  readonly av: AvatarKind;
  readonly icon: IconName;
}

const TONE_STYLES: Record<Tone, ToneStyle> = {
  neutral: { bg: T.surface, bd: T.border, av: 'sys', icon: 'bell' },
  safety: { bg: T.safetySoft, bd: '#E8D7A8', av: 'warn', icon: 'shield' },
  warning: { bg: T.safetySoft, bd: '#E8D7A8', av: 'warn', icon: 'spark' },
};

const ACTIONS: readonly { readonly action: NotificationAction; readonly label: string }[] = [
  { action: 'done', label: 'Сделано' },
  { action: 'snooze', label: 'Отложить' },
  { action: 'dismiss', label: 'Не делал' },
];

function toneFor(kind: NotificationKind): Tone {
  if (kind === 'safety') return 'safety';
  if (kind === 'replacement') return 'warning';
  return 'neutral';
}

function titleFor(kind: NotificationKind): string {
  switch (kind) {
    case 'safety':
      return 'Проверка безопасности';
    case 'replacement':
      return 'Плановое ТО';
    default:
      return 'Уведомление';
  }
}

// Фолбэк-текст, когда у уведомления не пришли название правила/оборудования.
function bodyFor(kind: NotificationKind): string {
  switch (kind) {
    case 'safety':
      return 'Проверка, важная для безопасности.';
    case 'replacement':
      return 'Подошёл срок планового обслуживания.';
    default:
      return 'Загляни в карточку оборудования.';
  }
}

/** Конкретная причина уведомления: правило ТО + оборудование (aidepo-7). */
function notifBody(row: NotificationRow): string {
  const parts: string[] = [];
  if (row.ruleTitle != null && row.ruleTitle.trim().length > 0) {
    parts.push(`«${row.ruleTitle}»`);
  }
  if (row.equipmentName != null && row.equipmentName.trim().length > 0) {
    parts.push(row.equipmentName);
  }
  return parts.length > 0 ? parts.join(' — ') : bodyFor(row.kind);
}

/** Дата планового ТО, человекочитаемо. */
function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
}

/** Русская подпись зафиксированного действия. */
function actionTakenLabel(action: string): string {
  if (action === 'done') return 'сделано';
  if (action === 'snooze') return 'отложено';
  if (action === 'dismiss') return 'не делал';
  return action;
}

function timeLabel(iso: string): string {
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return '';
  const diffMs = ts - Date.now();
  const days = Math.round(diffMs / 86_400_000);
  if (days === 0) return 'Сегодня';
  if (days === 1) return 'Завтра';
  if (days === -1) return 'Вчера';
  if (days > 1) return `Через ${days} дн.`;
  return `${Math.abs(days)} дн. назад`;
}

function describeError(err: unknown): string {
  return humanizeError(err, 'load');
}

type Status = 'loading' | 'ready' | 'error';

export function NotifsScreen({ router }: NotifsScreenProps): JSX.Element {
  const [status, setStatus] = useState<Status>('loading');
  const [rows, setRows] = useState<readonly NotificationRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<readonly string[]>([]);
  const [resolved, setResolved] = useState<ReadonlySet<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    setError(null);
    notifications
      .listNotifications()
      .then((res) => {
        if (cancelled) return;
        setRows(res.notifications);
        setStatus('ready');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(describeError(err));
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const fresh = useMemo(
    () => rows.filter((r) => r.actionTaken === null && !resolved.has(r.id)),
    [rows, resolved],
  );
  const earlier = useMemo(
    () => rows.filter((r) => r.actionTaken !== null || resolved.has(r.id)),
    [rows, resolved],
  );

  const act = (id: string, action: NotificationAction): void => {
    if (busy.includes(id)) return;
    setBusy((b) => [...b, id]);
    notifications
      .patchNotification(id, action)
      .then(() => {
        setResolved((s) => new Set(s).add(id));
      })
      .catch((err: unknown) => {
        setError(describeError(err));
      })
      .finally(() => {
        setBusy((b) => b.filter((x) => x !== id));
      });
  };

  const open = (row: NotificationRow): void => {
    if (row.equipmentId !== null) router.go('eq_card', { id: row.equipmentId });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: T.bg }}>
      {/* Уведомления — таб; router.back() из таба некуда вести (стек пуст),
          поэтому «Назад» уводит на Главный, откуда экран и открывают. */}
      <ScreenHeader
        title="Уведомления"
        subtitle="Напоминания о приближающемся ТО"
        onBack={() => router.go('feed')}
      />
      <div style={{ flex: 1, overflow: 'auto', padding: '4px 16px 24px' }}>
        {status === 'loading' && (
          <div style={{ fontSize: 12.5, color: T.ink3, padding: '6px 2px' }}>Загружаю…</div>
        )}
        {status === 'error' && error !== null && <ErrorBanner message={error} />}

        {status === 'ready' && rows.length === 0 && (
          <EmptyState text="Пока ничего нового — всё под контролем." />
        )}

        {status === 'ready' && rows.length > 0 && (
          <>
            {fresh.map((row) => (
              <NotifCard
                key={row.id}
                row={row}
                busy={busy.includes(row.id)}
                onAction={(a) => act(row.id, a)}
                onOpen={() => open(row)}
              />
            ))}
            {fresh.length === 0 && (
              <EmptyState text="Свежих уведомлений нет." />
            )}
            {earlier.length > 0 && (
              <>
                <SectionLabel style={{ padding: '16px 4px 6px' }}>Раньше</SectionLabel>
                {earlier.map((row) => (
                  <NotifCard
                    key={row.id}
                    row={row}
                    busy={false}
                    done
                    onAction={() => undefined}
                    onOpen={() => open(row)}
                  />
                ))}
              </>
            )}
            {status === 'ready' && error !== null && <ErrorBanner message={error} />}
          </>
        )}
      </div>
    </div>
  );
}

interface NotifCardProps {
  readonly row: NotificationRow;
  readonly busy: boolean;
  readonly done?: boolean;
  readonly onAction: (action: NotificationAction) => void;
  readonly onOpen: () => void;
}

function NotifCard({ row, busy, done = false, onAction, onOpen }: NotifCardProps): JSX.Element {
  const tone = TONE_STYLES[toneFor(row.kind)];
  const tappable = row.equipmentId !== null;
  return (
    <div
      onClick={tappable ? onOpen : undefined}
      style={{
        background: tone.bg,
        border: `1px solid ${tone.bd}`,
        borderRadius: 16,
        padding: 14,
        marginTop: 10,
        opacity: done ? 0.55 : 1,
        cursor: tappable ? 'pointer' : 'default',
      }}
    >
      <div style={{ display: 'flex', gap: 12 }}>
        <Avatar kind={tone.av} icon={tone.icon} size={36} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14.5, fontWeight: 700, color: T.ink, lineHeight: 1.3 }}>
            {titleFor(row.kind)}
          </div>
          <div style={{ fontSize: 13, color: T.ink2, marginTop: 4, lineHeight: 1.4 }}>
            {notifBody(row)}
          </div>
          <div style={{ fontSize: 11.5, color: T.ink3, marginTop: 6, fontWeight: 500 }}>
            {row.actionTaken !== null
              ? `Вы отметили: ${actionTakenLabel(row.actionTaken)}`
              : `Плановое ТО ${formatDate(row.scheduledFor)} · ${timeLabel(row.scheduledFor)}`}
          </div>
        </div>
      </div>
      {!done && (
        <div
          style={{
            display: 'flex',
            gap: 8,
            marginTop: 12,
            paddingTop: 12,
            borderTop: `1px solid ${tone.bd}`,
          }}
        >
          {ACTIONS.map((a, i) => (
            <button
              key={a.action}
              type="button"
              disabled={busy}
              onClick={(e) => {
                e.stopPropagation();
                onAction(a.action);
              }}
              style={{
                flex: 1,
                padding: '8px 6px',
                borderRadius: 10,
                background: i === 0 ? T.ink : T.surface,
                color: i === 0 ? '#fff' : T.ink,
                border: `1px solid ${i === 0 ? T.ink : T.border}`,
                fontFamily: T.font,
                fontSize: 12.5,
                fontWeight: 650,
                cursor: busy ? 'not-allowed' : 'pointer',
                opacity: busy ? 0.5 : 1,
              }}
            >
              {a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyState({ text }: { readonly text: string }): JSX.Element {
  return (
    <div
      style={{
        marginTop: 14,
        padding: '20px 14px',
        background: T.surfaceAlt,
        border: `1px solid ${T.border}`,
        borderRadius: 14,
        fontSize: 13,
        color: T.ink3,
        textAlign: 'center',
        fontWeight: 500,
      }}
    >
      {text}
    </div>
  );
}

function ErrorBanner({ message }: { readonly message: string }): JSX.Element {
  return (
    <div
      style={{
        marginTop: 12,
        padding: '10px 12px',
        background: T.dangerSoft,
        border: '1px solid #E9C7BF',
        borderRadius: 12,
        fontSize: 12.5,
        color: T.danger,
        fontWeight: 600,
      }}
    >
      {message}
    </div>
  );
}
